/* eslint-disable camelcase */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { handleMessages } from '../../_helpers/utilities'
import { IBusinessState } from '../../_models/superAdmin/businessInterface'
import {
  IBusinessFacilityParams,
  ISuperAdminFacilityState,
  ISuperAdminFacilityStatusDetails,
  ISuperAdminGetFacilityBookingCount,
} from '../../_models/superAdmin/facilityInterface'
import * as facilityService from '../../_services/superAdmin/facilityService'
import { RootState } from '../../_store/store'
import { startLoader, stopLoader } from '../loaderSlice'
import { errorToast, successToast } from '../toastSlice'

const initialState: ISuperAdminFacilityState = {
  facilities: {
    current_page: 1,
    data: [],
    total: 0,
    per_page: 6,
    last_page: 1,
  },
  facilityListFilters: {
    page: 1,
    per_page: 6,
    search: '',
  },
  isLoadingFacilityList: false,
  bookingsCount: 0,
}

export const getSuperAdminBusinessFacilities = createAsyncThunk(
  '/getSuperAdminBusinessFacilities',
  async (params: IBusinessFacilityParams, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getSuperAdminBusinessFacilities(params)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const updateSuperAdminFacilityStatus = createAsyncThunk(
  '/updateStatus',
  async (
    facilityStatus: ISuperAdminFacilityStatusDetails,
    { dispatch, rejectWithValue, getState },
  ) => {
    dispatch(startLoader())
      const { superAdminFacility, superAdminBusiness } = getState() as {
        superAdminFacility: ISuperAdminFacilityState
        superAdminBusiness: IBusinessState
      }
    try {
      const response = await facilityService.updateSuperAdminFacilityStatus(facilityStatus)
      dispatch(successToast(handleMessages(response.message)))
      dispatch(stopLoader())
      if (superAdminBusiness.businessDetailsId) {
        const params: IBusinessFacilityParams = {
          business_id: superAdminBusiness.businessDetailsId,
          ...superAdminFacility.facilityListFilters,
        }
        dispatch(getSuperAdminBusinessFacilities(params))
      }

      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)
export const getSuperAdminFacilityBookingCount = createAsyncThunk(
  '/getFacilityBookingCount',
  async (facilityDetails: ISuperAdminGetFacilityBookingCount, { dispatch, rejectWithValue }) => {
    dispatch(startLoader())
    try {
      const response = await facilityService.getSuperAdminFacilityBookingCount(facilityDetails)
      dispatch(stopLoader())
      return response
    } catch (err: any) {
      dispatch(stopLoader())
      dispatch(errorToast(handleMessages(err.message)))
      return rejectWithValue(err)
    }
  },
)

export const superAdminFacilitySlice = createSlice({
  name: 'superAdminFacility',
  initialState,
  reducers: {
    changeFilters: (state, action) => {
      state.facilityListFilters = { ...state.facilityListFilters, ...action.payload }
    },
    resetFilters: (state) => {
      state.facilityListFilters = initialState.facilityListFilters
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSuperAdminBusinessFacilities.fulfilled, (state, action) => {
      state.facilities = action.payload.data.facilities
      state.isLoadingFacilityList = false
    })
    builder.addCase(getSuperAdminBusinessFacilities.pending, (state) => {
      state.isLoadingFacilityList = true
    })
    builder.addCase(getSuperAdminBusinessFacilities.rejected, (state) => {
      state.isLoadingFacilityList = false
    })
    builder.addCase(getSuperAdminFacilityBookingCount.fulfilled, (state, action) => {
      state.bookingsCount = action.payload.data.bookings_count
    })
  },
})

export const { changeFilters, resetFilters } = superAdminFacilitySlice.actions
export const superAdminFacilityState = (state: RootState) => state.superAdminFacility
const superAdminFacilityReducer = superAdminFacilitySlice.reducer
export default superAdminFacilityReducer
